import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import Modal from '../components/Modal';
import { useAuth } from '../context/AuthContext';
import ZeitraumWahl, { aktuellesQuartal, heuteIso, zeitraumText, fmtDatum, fmtZahl, fmtEuro, ladeDatei } from '../components/ZeitraumWahl';
import { hinweis } from '../components/Hinweise';

const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function NachtragDialog({ onClose, onGespeichert }) {
  const [team, setTeam] = useState([]);
  const [gruppen, setGruppen] = useState([]);
  const [form, setForm] = useState({ mitglied_id: '', gruppe_id: '', datum: heuteIso(), von: '', bis: '' });
  const [zeitFehlt, setZeitFehlt] = useState(false);
  const [fehler, setFehler] = useState('');
  const [speichern, setSpeichern] = useState(false);
  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setFehler(''); if (k !== 'von' && k !== 'bis') setZeitFehlt(false); };

  useEffect(() => {
    Promise.all([api.get('/api/admin/team'), api.get('/api/admin/gruppen')])
      .then(([t, g]) => { setTeam(t.data.filter(m => m.aktiv)); setGruppen(g.data); })
      .catch(() => setFehler('Daten konnten nicht geladen werden'));
  }, []);

  const absenden = async () => {
    if (!form.mitglied_id || !form.gruppe_id || !form.datum) return setFehler('Bitte Person, Gruppe und Datum wählen');
    setSpeichern(true); setFehler('');
    try {
      const daten = { mitglied_id: form.mitglied_id, gruppe_id: form.gruppe_id, datum: form.datum };
      if (zeitFehlt) Object.assign(daten, { von: form.von, bis: form.bis });
      await api.post('/api/admin/stunden/einsaetze', daten);
      onGespeichert();
    } catch (err) {
      if (err.response?.data?.zeit_fehlt) setZeitFehlt(true);
      setFehler(err.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally { setSpeichern(false); }
  };

  return (
    <Modal title="Einsatz nachtragen" onClose={onClose} size="md">
      <div className="form-group"><label className="form-label">Person</label>
        <select className="form-control" value={form.mitglied_id} onChange={e => set('mitglied_id', e.target.value)} autoFocus>
          <option value="">– wählen –</option>
          {team.map(m => <option key={m.id} value={m.id}>{m.nachname}, {m.vorname} ({m.rolle_name})</option>)}
        </select></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.75rem' }}>
        <div className="form-group"><label className="form-label">Gruppe</label>
          <select className="form-control" value={form.gruppe_id} onChange={e => set('gruppe_id', e.target.value)}>
            <option value="">– wählen –</option>
            {gruppen.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select></div>
        <div className="form-group"><label className="form-label">Datum</label>
          <input type="date" className="form-control" value={form.datum} max={heuteIso()} onChange={e => set('datum', e.target.value)} />
          {form.datum && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{WOCHENTAGE[(new Date(`${form.datum}T12:00:00Z`).getUTCDay() + 6) % 7]}</div>}
        </div>
      </div>
      {zeitFehlt ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.75rem' }}>
          <div className="form-group"><label className="form-label">Von</label>
            <input type="time" className="form-control" value={form.von} onChange={e => set('von', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Bis</label>
            <input type="time" className="form-control" value={form.bis} onChange={e => set('bis', e.target.value)} /></div>
        </div>
      ) : (
        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Die Stunden ergeben sich aus der Trainingszeit der Gruppe an diesem Wochentag.</p>
      )}
      {fehler && <div className="alert alert-error">{fehler}</div>}
      <div className="modal-actions">
        <button className="btn btn-secondary btn-sm" onClick={onClose}>Abbrechen</button>
        <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={absenden} disabled={speichern}>
          {speichern ? 'Speichern…' : 'Nachtragen'}
        </button>
      </div>
    </Modal>
  );
}

export default function Stunden() {
  const { user } = useAuth();
  const [zeitraum, setZeitraum] = useState(aktuellesQuartal);
  const [daten, setDaten] = useState(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');
  const [offen, setOffen] = useState({});
  const [nachtrag, setNachtrag] = useState(false);

  const params = zeitraum.quartal ? { jahr: zeitraum.jahr, quartal: zeitraum.quartal } : { von: zeitraum.von, bis: zeitraum.bis };

  const load = useCallback(async () => {
    if (!zeitraum.quartal && (!zeitraum.von || !zeitraum.bis || zeitraum.von > zeitraum.bis)) return;
    setLaden(true); setFehler('');
    try {
      const r = await api.get('/api/admin/stunden', { params });
      setDaten(r.data);
    } catch (err) { setFehler(err.response?.data?.error || 'Daten konnten nicht geladen werden'); }
    finally { setLaden(false); }
  }, [JSON.stringify(zeitraum)]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const abgeschlossen = !!daten?.abschluss;
  const kannAbschliessen = zeitraum.quartal && !abgeschlossen && daten && daten.zeitraum.bis < heuteIso();

  const abschliessen = async () => {
    if (!window.confirm(`${zeitraumText(zeitraum)} abschließen?\n\nDie Stunden werden festgeschrieben. Einsätze in diesem Quartal können danach nicht mehr eingecheckt, nachgetragen oder gelöscht werden.`)) return;
    try { await api.post('/api/admin/stunden/abschluss', zeitraum); load(); }
    catch (err) { hinweis.fehler(err.response?.data?.error || 'Abschluss fehlgeschlagen'); }
  };
  const wiederOeffnen = async () => {
    if (!window.confirm(`${zeitraumText(zeitraum)} wieder öffnen?\n\nDie festgeschriebenen Zahlen werden verworfen und neu berechnet.`)) return;
    try { await api.delete(`/api/admin/stunden/abschluss/${zeitraum.jahr}/${zeitraum.quartal}`); load(); }
    catch (err) { hinweis.fehler(err.response?.data?.error || 'Fehlgeschlagen'); }
  };
  const loeschen = async (e) => {
    if (!window.confirm(`Einsatz von ${e.vorname} ${e.nachname} am ${fmtDatum(e.datum)} (${e.gruppe_name}) löschen?`)) return;
    try { await api.delete(`/api/admin/stunden/einsaetze/${e.id}`); load(); }
    catch (err) { hinweis.fehler(err.response?.data?.error || 'Löschen fehlgeschlagen'); }
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Trainer- und Helferstunden</h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {!abgeschlossen && <button className="btn btn-secondary btn-sm" onClick={() => setNachtrag(true)}>+ Einsatz nachtragen</button>}
          <button className="btn btn-secondary btn-sm" disabled={!daten}
            onClick={() => ladeDatei('/api/admin/stunden/export', params, `stunden_${zeitraum.quartal ? `${zeitraum.jahr}_Q${zeitraum.quartal}` : `${zeitraum.von}_bis_${zeitraum.bis}`}.xlsx`)}>
            📊 Excel
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem', padding: '0.75rem 1rem' }}>
        <ZeitraumWahl wert={zeitraum} onChange={setZeitraum} />
      </div>

      {fehler && <div className="alert alert-error">{fehler}</div>}

      {daten && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', fontSize: '0.88rem' }}>
          {abgeschlossen ? (
            <span style={{ background: '#e8f5e9', color: '#2e7d32', borderRadius: 12, padding: '3px 10px', fontWeight: 700 }}>
              🔒 Abgeschlossen am {new Date(daten.abschluss.abgeschlossen_am).toLocaleDateString('de-DE')}{daten.abschluss.von ? ` von ${daten.abschluss.von}` : ''}
            </span>
          ) : (
            <span style={{ background: '#fff8e1', color: '#e65100', borderRadius: 12, padding: '3px 10px', fontWeight: 700 }}>vorläufig</span>
          )}
          {kannAbschliessen && <button className="btn btn-primary btn-sm" onClick={abschliessen}>🔒 Quartal abschließen</button>}
          {abgeschlossen && user?.rolle === 'admin' && <button className="btn btn-secondary btn-sm" onClick={wiederOeffnen}>Wieder öffnen</button>}
        </div>
      )}

      {daten && (
        <div className="stat-grid">
          <div className="stat-card"><div className="stat-label">Stunden gesamt</div><div className="stat-value">{fmtZahl(daten.gesamt.stunden)}</div></div>
          <div className="stat-card"><div className="stat-label">Auszuzahlen</div><div className="stat-value">{fmtEuro(daten.gesamt.betrag)}</div></div>
          <div className="stat-card"><div className="stat-label">Einsätze</div><div className="stat-value">{daten.gesamt.einsaetze}</div></div>
          <div className="stat-card"><div className="stat-label">Personen</div><div className="stat-value">{daten.gesamt.personen}</div></div>
        </div>
      )}

      {daten?.gesamt?.einsaetze_ohne_satz > 0 && (
        <div className="alert" style={{ background: '#fff8e1', color: '#e65100', border: '1px solid #ffe082' }}>
          {daten.gesamt.einsaetze_ohne_satz} Einsätze mit {fmtZahl(daten.gesamt.stunden_ohne_satz)} Stunden haben keinen
          Stundensatz und zählen mit 0,00 €. Betroffene Rollen: <strong>{daten.gesamt.rollen_ohne_satz.join(', ')}</strong>.
          Der Satz lässt sich unter „Rollen &amp; Vergütung" nachtragen – er gilt ab dem gewählten Datum.
        </div>
      )}

      <div className="card">
        {laden ? <div style={{ padding: '2rem', textAlign: 'center' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div> : daten && (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Person</th><th>Rolle</th><th>Einsätze</th><th>Stunden</th><th>Auszuzahlen</th><th>Je Gruppe</th></tr></thead>
              <tbody>
                {daten.personen.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>Keine Einsätze im Zeitraum</td></tr>
                )}
                {daten.personen.map(p => (
                  <React.Fragment key={p.mitglied_id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => setOffen(o => ({ ...o, [p.mitglied_id]: !o[p.mitglied_id] }))}>
                      <td style={{ fontWeight: 600 }}>{offen[p.mitglied_id] ? '▾' : '▸'} {p.nachname}, {p.vorname}</td>
                      <td>
                        {p.rolle}
                        {/* Bei wechselndem Satz im Zeitraum: Aufteilung zeigen, damit die Summe nachvollziehbar bleibt */}
                        {(p.posten || []).length > 1 && (
                          <div className="td-muted" style={{ fontSize: '0.75rem' }}>
                            {p.posten.map(o => `${fmtZahl(o.stunden)} × ${o.satz === null ? '–' : fmtEuro(o.satz)}`).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td>{p.einsaetze}</td>
                      <td style={{ fontWeight: 700 }}>{fmtZahl(p.stunden)}</td>
                      <td style={{ fontWeight: 700 }}>
                        {fmtEuro(p.betrag)}
                        {p.stunden_ohne_satz > 0 && (
                          <div style={{ fontSize: '0.72rem', color: '#e65100', fontWeight: 600 }}>
                            {fmtZahl(p.stunden_ohne_satz)} Std. ohne Satz
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize: '0.8rem' }}>{p.gruppen.map(g => `${g.gruppe}: ${fmtZahl(g.stunden)}`).join(' · ')}</td>
                    </tr>
                    {offen[p.mitglied_id] && daten.einsaetze.filter(e => e.mitglied_id === p.mitglied_id).map(e => (
                      <tr key={e.id} style={{ background: '#fafbfc', fontSize: '0.82rem' }}>
                        <td style={{ paddingLeft: '2rem' }}>{fmtDatum(e.datum)}</td>
                        <td>{e.gruppe_name}</td>
                        <td>{e.von}–{e.bis}</td>
                        <td>{fmtZahl(e.stunden)}</td>
                        <td>{e.satz === null || e.satz === undefined
                          ? <span style={{ color: '#e65100' }}>kein Satz</span>
                          : `${fmtEuro(e.satz)} → ${fmtEuro(e.betrag)}`}</td>
                        <td>
                          <span className="td-muted">{e.quelle === 'nachtrag' ? 'Nachtrag' : 'Training'}{e.erfasst_von ? ` · ${e.erfasst_von}` : ''}</span>
                          {!abgeschlossen && (
                            <button className="btn btn-danger btn-sm" style={{ marginLeft: 8, padding: '1px 8px' }} onClick={() => loeschen(e)}>✕</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {nachtrag && <NachtragDialog onClose={() => setNachtrag(false)} onGespeichert={() => { setNachtrag(false); load(); }} />}
    </div>
  );
}
